import React from 'react'
import { motion } from 'framer-motion'

interface PanelShellProps {
  title: string
  subtitle?: string
  eyebrow?: string
  actions?: React.ReactNode
  children: React.ReactNode
  className?: string
  /**
   * 'default' (every existing page, unchanged) renders the plain text
   * header this component has always had. 'teal' additionally wraps it in
   * a rounded deep-teal card — used only by the client Dashboard, as part
   * of the brand color-consistency pass, so its "Welcome back" header
   * reads as a continuation of the site's teal/cream rhythm. Purely
   * additive: no other page passes this prop, so nothing else changes.
   */
  headerVariant?: 'default' | 'teal'
}

const PanelShell: React.FC<PanelShellProps> = ({
  title,
  subtitle,
  eyebrow = 'Fit by Cassie',
  actions,
  children,
  className = '',
  headerVariant = 'default',
}) => {
  const teal = headerVariant === 'teal'
  return (
    <div className={`panel-page ${className}`}>
      <div className="panel-container">
        <motion.header
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className={teal ? 'panel-header bg-brand-navy text-white rounded-3xl px-6 py-7 sm:px-10 sm:py-9' : 'panel-header'}
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              {eyebrow && (
                <p className={teal ? 'text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-3' : 'eyebrow mb-3'}>
                  {eyebrow}
                </p>
              )}
              <h1 className={teal ? 'font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight text-white' : 'panel-title'}>
                {title}
              </h1>
              {subtitle && (
                <p className={teal ? 'mt-2 text-sm sm:text-base text-brand-cream/80 max-w-2xl' : 'panel-subtitle'}>
                  {subtitle}
                </p>
              )}
            </div>
            {actions && (
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                {actions}
              </div>
            )}
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  )
}

export default PanelShell
